import React, { useState, useEffect } from 'react';
import { postData } from '../../services/api.service';

/**
 * Componente para calcular y mostrar interés en tiempo real
 * Se usa en el formulario de creación/edición de pedidos
 * Permite edición manual del interés para ajustar según posnet/terminal usado
 */
function InterestCalculator({ 
  orderTotal, 
  paymentFormId, 
  installments, 
  onInterestCalculated,
  initialInterestData = null // Interés previo (al editar pedido existente)
}) {
  const [calculating, setCalculating] = useState(false);
  const [interestInfo, setInterestInfo] = useState(initialInterestData);
  const [error, setError] = useState(null);
  const [manualMode, setManualMode] = useState(false); // Modo edición manual
  const [manualInterestRate, setManualInterestRate] = useState(initialInterestData?.interest_rate || 0);
  const [inputError, setInputError] = useState(false); // Error en el input manual
  const [hasInitialized, setHasInitialized] = useState(false); // Flag para controlar inicialización

  // Cargar datos iniciales solo una vez
  useEffect(() => {
    if (initialInterestData && !hasInitialized) {
      setInterestInfo(initialInterestData);
      setManualInterestRate(initialInterestData.interest_rate || 0);
      setHasInitialized(true);
      // Notificar al padre inmediatamente
      if (onInterestCalculated) {
        onInterestCalculated(initialInterestData);
      }
    }
  }, [initialInterestData, hasInitialized, onInterestCalculated]);

  useEffect(() => {
    // NUNCA calcular automáticamente
    // El interés debe agregarse manualmente por el vendedor
    // Solo preservar si ya existe (al editar pedido)
    
    // Limpiar si faltan datos básicos (pero solo en pedidos nuevos)
    if ((!orderTotal || !paymentFormId || !installments) && !initialInterestData) {
      setInterestInfo(null);
      setError(null);
      setManualMode(false);
    }
  }, [orderTotal, paymentFormId, installments, initialInterestData]);

  const calculateInterest = async () => {
    try {
      setCalculating(true);
      setError(null);

      const response = await postData('/api/interest/calculate', {
        order_total: orderTotal,
        payment_form_id: paymentFormId,
        installments: installments
      }, false);

      if (response?.data?.success) {
        const calculatedData = response.data.data;
        setInterestInfo(calculatedData);
        setManualInterestRate(calculatedData.interest_rate); // Guardar para edición manual
        // Notificar al componente padre
        if (onInterestCalculated) {
          onInterestCalculated(calculatedData);
        }
      } else {
        const errorMsg = response?.data?.msg || response?.response?.data?.msg || 'Error al calcular interés';
        console.error('Error calculando interés:', errorMsg, response);
        
        // Mejorar el mensaje si es un error de cuotas fuera de rango
        let friendlyError = errorMsg;
        if (errorMsg.includes('fuera de rango') || errorMsg.includes('out of range')) {
          friendlyError = `⚠️ ${errorMsg}\n\nEsta forma de pago no admite ${installments} cuotas. Por favor:\n• Cambia el número de cuotas a un valor permitido, o\n• Selecciona otra forma de pago (ej: Tarjeta de Crédito para cuotas con interés)`;
        }
        
        setError(friendlyError);
        setInterestInfo(null);
      }
    } catch (err) {
      console.error('Error calculando interés:', err);
      const errorMsg = err.response?.data?.msg || err.message || 'Error al calcular interés';
      
      // Mejorar el mensaje si es un error de cuotas
      let friendlyError = errorMsg;
      if (errorMsg.includes('fuera de rango') || errorMsg.includes('out of range')) {
        friendlyError = `⚠️ ${errorMsg}\n\nEsta forma de pago no admite ${installments} cuotas. Por favor:\n• Cambia el número de cuotas a un valor permitido, o\n• Selecciona otra forma de pago (ej: Tarjeta de Crédito para cuotas con interés)`;
      }
      
      setError(friendlyError);
      setInterestInfo(null);
    } finally {
      setCalculating(false);
    }
  };

  const handleManualInterestChange = (e) => {
    const inputValue = e.target.value;
    
    // Permitir string vacío para poder borrar completamente
    if (inputValue === '') {
      setManualInterestRate('');
      setInputError(false);
      
      // Recalcular con 0% de interés
      const manualCalculatedData = {
        order_total: orderTotal,
        interest_rate: 0,
        interest_amount: 0,
        total_with_interest: orderTotal,
        installment_amount: orderTotal / installments,
        installments: installments,
        description: 'Sin interés'
      };

      setInterestInfo(manualCalculatedData);
      
      if (onInterestCalculated) {
        onInterestCalculated(manualCalculatedData);
      }
      return;
    }

    // Validar que sea un número válido (permite decimales con punto o coma)
    const sanitizedValue = inputValue.replace(',', '.');
    const newRate = parseFloat(sanitizedValue);
    
    // Si no es un número válido, mostrar error pero mantener el input
    if (isNaN(newRate)) {
      setManualInterestRate(inputValue); // Mantener lo que escribió el usuario
      setInputError(true);
      return;
    }

    // Validar rango (0-100%)
    if (newRate < 0 || newRate > 100) {
      setInputError(true);
      setManualInterestRate(inputValue);
      return;
    }

    // Todo OK - limpiar error
    setInputError(false);
    setManualInterestRate(newRate);
    
    // Recalcular con el nuevo interés
    const interestAmount = orderTotal * (newRate / 100);
    const totalWithInterest = orderTotal + interestAmount;
    const installmentAmount = totalWithInterest / installments;

    const manualCalculatedData = {
      order_total: orderTotal,
      interest_rate: newRate,
      interest_amount: interestAmount,
      total_with_interest: totalWithInterest,
      installment_amount: installmentAmount,
      installments: installments,
      description: newRate > 0 ? `${newRate}% interés (ajustado manualmente)` : 'Sin interés'
    };

    setInterestInfo(manualCalculatedData);
    
    // Notificar al componente padre
    if (onInterestCalculated) {
      onInterestCalculated(manualCalculatedData);
    }
  };

  const handleKeyDown = (e) => {
    // ESC para cancelar y volver a modo automático
    if (e.key === 'Escape' && manualMode) {
      toggleManualMode();
    }
  };

  const toggleManualMode = () => {
    if (!manualMode) {
      // Activar modo manual
      setManualMode(true);
      setInputError(false);
    } else {
      // Volver a cálculo automático
      setManualMode(false);
      setInputError(false);
      calculateInterest();
    }
  };

  const removeInterest = () => {
    // Limpiar el interés completamente
    setInterestInfo(null);
    setManualMode(false);
    setManualInterestRate(0);
    // Notificar al padre que no hay interés
    if (onInterestCalculated) {
      onInterestCalculated(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (!paymentFormId || !orderTotal || orderTotal <= 0) {
    return null;
  }

  return (
    <div className="interest-calculator mt-3">
      {calculating && (
        <div className="alert alert-info">
          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
          Calculando interés...
        </div>
      )}

      {error && (
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {/* Mensaje cuando NO hay interés calculado/guardado */}
      {!interestInfo && !calculating && !error && (
        <div className="alert alert-light border">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <i className="bi bi-info-circle me-2 text-primary"></i>
              <strong>Sin interés</strong>
              <p className="mb-0 mt-1 small text-muted">
                Este pedido no tiene interés. Si se pagó con tarjeta y el posnet cobra interés, 
                presioná "Calcular Interés" para agregarlo.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-primary btn-sm ms-3"
              onClick={calculateInterest}
              title="Calcular interés según forma de pago"
            >
              <i className="bi bi-calculator me-1"></i>
              Calcular Interés
            </button>
          </div>
        </div>
      )}

      {interestInfo && !calculating && !error && (
        <div className="card border-primary">
          <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="bi bi-calculator me-2"></i>
              Detalle Financiero {manualMode && <span className="badge bg-warning text-dark ms-2">Manual</span>}
            </h6>
            <div className="btn-group btn-group-sm">
              <button 
                type="button"
                className="btn btn-light"
                onClick={toggleManualMode}
                title={manualMode ? "Volver a cálculo automático" : "Ajustar interés manualmente"}
              >
                <i className={`bi ${manualMode ? 'bi-arrow-counterclockwise' : 'bi-pencil'}`}></i>
                {manualMode ? ' Auto' : ' Editar'}
              </button>
              <button 
                type="button"
                className="btn btn-danger"
                onClick={removeInterest}
                title="Quitar interés del pedido"
              >
                <i className="bi bi-x-circle"></i>
                Quitar
              </button>
            </div>
          </div>
          <div className="card-body">
            {/* Modo Manual: Input para editar interés */}
            {manualMode && (
              <div className="alert alert-warning mb-3">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <label className="form-label mb-1">
                      <strong>Interés Real del Posnet/Terminal (%):</strong>
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        className={`form-control ${inputError ? 'is-invalid' : ''}`}
                        value={manualInterestRate}
                        onChange={handleManualInterestChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Ej: 18.5"
                        autoFocus
                      />
                      <span className="input-group-text">%</span>
                      {inputError && (
                        <div className="invalid-feedback">
                          Ingrese un número entre 0 y 100
                        </div>
                      )}
                    </div>
                    <small className="text-muted">
                      Puede usar decimales (ej: 18.5 o 18,5). Presione ESC para cancelar.
                    </small>
                  </div>
                  <div className="col-md-6">
                    <small className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      Ingrese el interés exacto que cobra el posnet/terminal usado 
                      (puede variar según MercadoPago, Prisma, etc.)
                    </small>
                  </div>
                </div>
              </div>
            )}

            {/* Total sin interés */}
            <div className="row mb-2">
              <div className="col-7">
                <span className="text-muted">Total sin interés:</span>
              </div>
              <div className="col-5 text-end">
                <strong>{formatCurrency(interestInfo.order_total)}</strong>
              </div>
            </div>

            {/* Interés */}
            {interestInfo.interest_rate > 0 && (
              <>
                <div className="row mb-2">
                  <div className="col-7">
                    <span className="text-muted">
                      Interés ({interestInfo.interest_rate}%):
                    </span>
                  </div>
                  <div className="col-5 text-end">
                    <strong className="text-warning">
                      + {formatCurrency(interestInfo.interest_amount)}
                    </strong>
                  </div>
                </div>

                <hr className="my-2" />

                {/* Total con interés */}
                <div className="row mb-3">
                  <div className="col-7">
                    <span className="fw-bold">Total a pagar:</span>
                  </div>
                  <div className="col-5 text-end">
                    <span className="fs-5 fw-bold text-success">
                      {formatCurrency(interestInfo.total_with_interest)}
                    </span>
                  </div>
                </div>

                {/* Monto por cuota */}
                {interestInfo.installments > 1 && (
                  <div className="alert alert-light mb-0">
                    <div className="row align-items-center">
                      <div className="col-6">
                        <small className="text-muted">
                          <i className="bi bi-credit-card me-1"></i>
                          {interestInfo.installments} cuotas de:
                        </small>
                      </div>
                      <div className="col-6 text-end">
                        <strong className="text-primary">
                          {formatCurrency(interestInfo.installment_amount)}
                        </strong>
                      </div>
                    </div>
                  </div>
                )}

                {/* Descripción */}
                {interestInfo.description && (
                  <div className="mt-2">
                    <small className="text-muted fst-italic">
                      <i className="bi bi-info-circle me-1"></i>
                      {interestInfo.description}
                    </small>
                  </div>
                )}
              </>
            )}

            {/* Sin interés */}
            {interestInfo.interest_rate === 0 && (
              <div className="alert alert-success mb-0">
                <i className="bi bi-check-circle me-2"></i>
                <strong>Sin interés</strong>
                {interestInfo.installments > 1 && (
                  <>
                    <br />
                    <small>
                      {interestInfo.installments} cuotas de{' '}
                      {formatCurrency(interestInfo.installment_amount)}
                    </small>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default InterestCalculator;
